// src/components/TiebreakerCard.js
//
// Renders the championship-score tiebreaker section above the picks UI.
// Two number inputs (team1 / team2 final score) with save-on-blur,
// debounced, plus a small "Saved" indicator that fades the way the
// pick-save feedback does on PickSubmission.
//
// Used by both PickSubmission (the participant flow) and AdminPickEntry
// (admin acting on someone's behalf). Both pages already have a hook
// (usePickState) for picks themselves; tiebreaker is one entry-level
// field pair, so it doesn't earn its own hook -- this component owns
// its draft state and persists via the setTiebreaker API.
//
// Props:
//   - entry            : the active entry. Must have entry_id,
//                        tiebreaker_team1_score, tiebreaker_team2_score.
//                        Null while loading; component renders nothing.
//   - championshipGame : the championship-round game row from
//                        GET /games, if available. Used purely for
//                        labels: when team1_name / team2_name are
//                        populated (the bracket has advanced far
//                        enough), we show "Duke" / "Houston" above
//                        the inputs instead of "Team 1" / "Team 2".
//                        Falls back gracefully when the prop is
//                        absent or its names are null.
//   - championshipFinal: true once the tournament's championship game
//                        has status === 'final'. When true the inputs
//                        become read-only with a "Locked" badge.
//   - onSaved          : called with the saved row after a successful
//                        PUT so the parent can refresh its local entry
//                        snapshot. Optional.
//   - onFeedback       : (msg, type?) -- surfaces error messages, since
//                        the only error case (network/server) is rare
//                        but worth not swallowing.

import React, { useState, useEffect, useRef } from 'react';
import { setTiebreaker } from '../services/api';

const SAVE_DEBOUNCE_MS = 400;

// Empty string for the input when the value is null, since a controlled
// <input type="number"> with a null value warns. Range 0..200 mirrors
// the backend zod schema.
function fieldToInput(v) { return v == null ? '' : String(v); }
function inputToField(s) {
    const trimmed = s.trim();
    if (trimmed === '') return null;
    const n = Number(trimmed);
    return Number.isInteger(n) ? n : NaN;
}

function TiebreakerCard({ entry, championshipGame, championshipFinal, onSaved, onFeedback }) {
    const [team1Draft,  setTeam1Draft]  = useState('');
    const [team2Draft,  setTeam2Draft]  = useState('');
    const [savedFlash,  setSavedFlash]  = useState(false);
    const [validationError, setValidationError] = useState('');
    const flashTimer = useRef(null);

    // When the entry changes (admin switches participants, or initial
    // load), refresh the draft inputs from the entry's saved values.
    // Without this, switching from a participant who entered 78-72 to
    // one who entered nothing would leave the previous draft visible.
    useEffect(() => {
        if (!entry) return;
        setTeam1Draft(fieldToInput(entry.tiebreaker_team1_score));
        setTeam2Draft(fieldToInput(entry.tiebreaker_team2_score));
        setValidationError('');
    }, [entry]);

    // Debounce-on-blur save: when the user tabs away from a field, wait
    // briefly to coalesce a rapid blur-and-back, then PUT only the
    // fields that have actually changed from the entry's saved values.
    const saveField = (field, draftValue) => {
        if (championshipFinal || !entry) return;

        const parsed = inputToField(draftValue);
        if (Number.isNaN(parsed)) {
            setValidationError('Scores must be whole numbers');
            return;
        }
        if (parsed !== null && (parsed < 0 || parsed > 200)) {
            setValidationError('Scores must be between 0 and 200');
            return;
        }
        setValidationError('');

        const savedValue = field === 'team1_score'
            ? entry.tiebreaker_team1_score
            : entry.tiebreaker_team2_score;
        // No-op if unchanged. Avoids a noisy PUT on every tab-out.
        if (parsed === savedValue) return;

        clearTimeout(flashTimer.current);
        setTiebreaker(entry.entry_id, { [field]: parsed })
            .then(res => {
                if (onSaved) onSaved(res.data);
                setSavedFlash(true);
                flashTimer.current = setTimeout(() => setSavedFlash(false), 2000);
            })
            .catch(err => {
                const msg = err.response?.data?.error || 'Failed to save tiebreaker';
                if (onFeedback) onFeedback(msg, 'error');
            });
    };

    useEffect(() => () => clearTimeout(flashTimer.current), []);

    if (!entry) return null;

    // Use real team names when the bracket has advanced enough that
    // they're populated on the championship game row. Fall back to
    // generic labels otherwise -- early in the tournament the
    // championship row is a placeholder with team1_id / team2_id NULL.
    const team1Label = championshipGame?.team1_name || 'Team 1';
    const team2Label = championshipGame?.team2_name || 'Team 2';

    return (
        <div className="bg-gray-900 border border-gray-700 rounded-xl p-4 mb-3">
            <div className="flex items-center justify-between mb-2">
                <div>
                    <p className="text-white font-bold text-sm">Championship Tiebreaker</p>
                    <p className="text-gray-500 text-xs">
                        Predict the final score. Used only to break leaderboard ties when the championship ends.
                    </p>
                </div>
                {championshipFinal && (
                    <span className="text-xs font-bold px-2 py-1 rounded bg-gray-800 text-gray-400 border border-gray-700">
                        Locked
                    </span>
                )}
                {!championshipFinal && savedFlash && (
                    <span className="text-xs font-bold text-green-400">Saved</span>
                )}
            </div>

            <div className="flex items-end gap-3">
                <div className="flex flex-col gap-1">
                    <span className="text-xs text-gray-400 font-bold">{team1Label}</span>
                    <input
                        type="number"
                        inputMode="numeric"
                        min={0}
                        max={200}
                        placeholder={team1Label}
                        value={team1Draft}
                        onChange={e => setTeam1Draft(e.target.value)}
                        onBlur={() => setTimeout(() => saveField('team1_score', team1Draft), SAVE_DEBOUNCE_MS)}
                        disabled={championshipFinal}
                        className={'w-24 bg-gray-800 border rounded-lg px-3 py-2 text-white text-sm font-bold text-center focus:outline-none ' +
                            (championshipFinal
                                ? 'border-gray-800 opacity-60'
                                : 'border-gray-700 focus:border-[var(--org-accent,#f97316)]')}
                    />
                </div>
                <span className="text-gray-500 text-sm font-bold pb-2">vs</span>
                <div className="flex flex-col gap-1">
                    <span className="text-xs text-gray-400 font-bold">{team2Label}</span>
                    <input
                        type="number"
                        inputMode="numeric"
                        min={0}
                        max={200}
                        placeholder={team2Label}
                        value={team2Draft}
                        onChange={e => setTeam2Draft(e.target.value)}
                        onBlur={() => setTimeout(() => saveField('team2_score', team2Draft), SAVE_DEBOUNCE_MS)}
                        disabled={championshipFinal}
                        className={'w-24 bg-gray-800 border rounded-lg px-3 py-2 text-white text-sm font-bold text-center focus:outline-none ' +
                            (championshipFinal
                                ? 'border-gray-800 opacity-60'
                                : 'border-gray-700 focus:border-[var(--org-accent,#f97316)]')}
                    />
                </div>
            </div>

            {validationError && (
                <p className="text-red-400 text-xs mt-2">{validationError}</p>
            )}
        </div>
    );
}

export default TiebreakerCard;